import { Injectable } from '@angular/core';
import { ConfigService } from 'providers/config.service';
import { AuthService } from 'providers/auth.service';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class StatusApiService {
    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private config: ConfigService
    ) {}

    /**
     * Get active & recent alerts of a charger
     */
    async getStationAlerts(stationId: string): Promise<GetStationAlertsResponse> {
        return this.http
            .get<GetStationAlertsResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/alerts`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Get alert history of a charger
     */
    async getStationAlertsHistory(
        stationId: string,
        queryParams?: GetStationAlertsHistoryQueryParameters
    ): Promise<GetStationAlertsHistoryResponse> {
        return this.http
            .get<GetStationAlertsHistoryResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/alerts/history?${Object.keys(
                    queryParams || {}
                )
                    .reduce(
                        (acc, k) =>
                            queryParams[k] !== undefined ? [...acc, `${k}=${encodeURIComponent(queryParams[k])}`] : acc,
                        []
                    )
                    .join('&')}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Get active & recent alerts
     */
    async getAlerts(): Promise<GetAlertsResponse> {
        return this.http
            .get<GetAlertsResponse>(`${this.config.restApiUrl}/alerts`, {
                headers: {
                    Authorization: `Bearer ${this.authService.jwt}`,
                },
            })
            .toPromise();
    }

    /**
     * Get alert by id
     */
    async getAlert(id: string): Promise<GetAlertResponse> {
        return this.http
            .get<GetAlertResponse>(`${this.config.restApiUrl}/alert/${encodeURIComponent(id)}`, {
                headers: {
                    Authorization: `Bearer ${this.authService.jwt}`,
                },
            })
            .toPromise();
    }

    /**
     * Update EVSE status
     */
    async putEvseStatus(stationId: string, evseId: string, body: PutEvseStatusRequest): Promise<PutEvseStatusResponse> {
        return this.http
            .put<PutEvseStatusResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/evses/${encodeURIComponent(evseId)}/status`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Update EVSE alert status
     */
    async putEvseAlert(stationId: string, evseId: string, body: PutEvseAlertRequest): Promise<PutEvseAlertResponse> {
        return this.http
            .put<PutEvseAlertResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/evses/${encodeURIComponent(evseId)}/alert`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Clear EVSE alert status
     */
    async clearEvseAlert(stationId: string, evseId: string, body: ClearEvseAlertRequest): Promise<any> {
        return this.http
            .put<any>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/evses/${encodeURIComponent(evseId)}/alert/clear`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Update Charging station's status
     */
    async putChargingStationStatus(
        stationId: string,
        body: PutChargingStationStatusRequest
    ): Promise<PutChargingStationStatusResponse> {
        return this.http
            .put<PutChargingStationStatusResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/status`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Update Charging station's alert status
     */
    async putChargingStationAlert(
        stationId: string,
        body: PutChargingStationAlertRequest
    ): Promise<PutChargingStationAlertResponse> {
        return this.http
            .put<PutChargingStationAlertResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/alert`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Clears Charging station's alert status
     */
    async clearChargingStationAlert(stationId: string, body: ClearChargingStationAlertRequest): Promise<any> {
        return this.http
            .put<any>(`${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/alert/clear`, body, {
                headers: {
                    Authorization: `Bearer ${this.authService.jwt}`,
                },
            })
            .toPromise();
    }

    /**
     * Get EVSE current status
     */
    async getEvseStatus(stationId: string, evseId: string): Promise<GetEvseStatusResponse> {
        return this.http
            .get<GetEvseStatusResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/evses/${encodeURIComponent(evseId)}/status`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Get EVSE status history
     */
    async getEvseStatusHistory(
        stationId: string,
        evseId: string,
        queryParams?: GetEvseStatusHistoryQueryParameters
    ): Promise<GetEvseStatusHistoryResponse> {
        return this.http
            .get<GetEvseStatusHistoryResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/evses/${encodeURIComponent(evseId)}/status-history?${Object.keys(
                    queryParams || {}
                )
                    .reduce(
                        (acc, k) =>
                            queryParams[k] !== undefined ? [...acc, `${k}=${encodeURIComponent(queryParams[k])}`] : acc,
                        []
                    )
                    .join('&')}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Get Charging station status
     */
    async getChargingStationStatus(stationId: string): Promise<GetChargingStationStatusResponse> {
        return this.http
            .get<GetChargingStationStatusResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/status`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Clears Charging station's alert status
     */
    async clearLocationAlert(locationUid: string, body: ClearLocationAlertRequest): Promise<any> {
        return this.http
            .put<any>(`${this.config.restApiUrl}/locations/${encodeURIComponent(locationUid)}/alert/clear`, body, {
                headers: {
                    Authorization: `Bearer ${this.authService.jwt}`,
                },
            })
            .toPromise();
    }

    /**
     * Get active & recent alerts of a location
     */
    async getLocationAlerts(locationUid: string): Promise<GetLocationAlertsResponse> {
        return this.http
            .get<GetLocationAlertsResponse>(
                `${this.config.restApiUrl}/locations/${encodeURIComponent(locationUid)}/alerts`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Get alert history of a location
     */
    async getLocationAlertsHistory(
        locationUid: string,
        queryParams?: GetLocationAlertsHistoryQueryParameters
    ): Promise<GetLocationAlertsHistoryResponse> {
        return this.http
            .get<GetLocationAlertsHistoryResponse>(
                `${this.config.restApiUrl}/locations/${encodeURIComponent(locationUid)}/alerts/history?${Object.keys(
                    queryParams || {}
                )
                    .reduce(
                        (acc, k) =>
                            queryParams[k] !== undefined ? [...acc, `${k}=${encodeURIComponent(queryParams[k])}`] : acc,
                        []
                    )
                    .join('&')}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Update Location's alert status
     */
    async putLocationAlert(locationUid: string, body: PutLocationAlertRequest): Promise<PutLocationAlertResponse> {
        return this.http
            .put<PutLocationAlertResponse>(
                `${this.config.restApiUrl}/locations/${encodeURIComponent(locationUid)}/alert`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }
}

export interface ClearChargingStationAlertRequest {
    type: string;
    timestamp: string;
}

export interface ClearEvseAlertRequest {
    type: string;
    timestamp: string;
}

export interface ClearLocationAlertRequest {
    type: string;
    timestamp: string;
}

export interface GetAlertResponse {
    alert: CeAlert;
}

export interface GetAlertsResponse {
    activeAlerts: CeAlert[];
    recentAlerts: CeAlert[];
}

export interface GetChargingStationStatusResponse {
    status: ChargingStationStatus;
    statusDate: string;
    /**
     * Reason for why the status is unavailable
     */
    reason?: string;
}

export interface GetEvseStatusResponse {
    /**
     * Current status of the device.
     */
    status: 'Available' | 'Charging' | 'Faulted' | 'Occupied' | 'Reserved' | 'Unavailable' | 'Unknown';
    /**
     * Date when the status became active.
     */
    statusDate: string;
    /**
     * Reason for why the status is unavailable
     */
    reason?: string;
    /**
     * More detailed reason why status is unavailable
     */
    detail?: string;
}

export interface GetEvseStatusHistoryQueryParameters {
    startTime?: string;
    endTime?: string;
}

export interface GetEvseStatusHistoryResponse {
    rows: StatusHistoryRow[];
}

export interface GetLocationAlertsResponse {
    activeAlerts: CeAlert[];
    recentAlerts: CeAlert[];
}

export interface GetLocationAlertsHistoryResponse {
    history: CeAlert[];
    lastVersion?: string;
}

export interface GetLocationAlertsHistoryQueryParameters {
    limit?: number;
    lastVersion?: string;
}

export interface GetStationAlertsResponse {
    activeAlerts: CeAlert[];
    recentAlerts: CeAlert[];
}

export interface GetStationAlertsHistoryResponse {
    history: CeAlert[];
    lastVersion?: string;
}

export interface GetStationAlertsHistoryQueryParameters {
    limit?: number;
    lastVersion?: string;
}

export interface PutChargingStationAlertRequest {
    /**
     * error message title
     */
    title?: string;
    /**
     * synthetic alert id
     */
    id?: string;
    /**
     * When alert became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    timestamp: string;
    /**
     * Type/code of the alert
     */
    type: string;
    /**
     * Error message, or other details
     */
    message?: string;
    /**
     * Error code
     */
    errorCode?: string;
}

export interface PutChargingStationAlertResponse {
    success: true;
}

export interface PutChargingStationStatusRequest {
    /**
     * Status of the device.
     */
    status: 'Faulted' | 'Operative' | 'Unavailable';
    /**
     * Date when the status became active.
     */
    statusDate: string;
    /**
     * If true, this status is not as "important", and may be overriden.
     * Used by for example onOcppConnect handler, which sets status to Unavailable
     * before being updated by a status from charging station (NotifyEvent OCPP message).
     * Sometimes however, the status from charging station is dated before the OCPP connection was established.
     */
    overridable?: boolean;
    /**
     * Reason for why the status is unavailable
     */
    reason?: string;
}

export interface PutChargingStationStatusResponse {
    success: true;
}

export interface PutEvseAlertRequest {
    /**
     * error message title
     */
    title?: string;
    /**
     * synthetic alert id
     */
    id?: string;
    /**
     * When alert became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    timestamp: string;
    /**
     * Type/code of the alert
     */
    type: string;
    /**
     * Error message, or other details
     */
    message?: string;
    /**
     * Error code
     */
    errorCode?: string;
}

export interface PutEvseAlertResponse {
    success: true;
}

export interface PutEvseStatusRequest {
    /**
     * Status of the device.
     */
    status: 'Available' | 'Charging' | 'Faulted' | 'Occupied' | 'Reserved' | 'Unavailable' | 'Unknown';
    /**
     * Date when the status became active.
     */
    statusDate: string;
    /**
     * If true, this status is not as "important", and may be overriden.
     * Used by for example onOcppConnect handler, which sets status to Unavailable
     * before being updated by a status from charging station (NotifyEvent OCPP message).
     * Sometimes however, the status from charging station is dated before the OCPP connection was established.
     */
    overridable?: boolean;
    /**
     * Reason for why the status is unavailable
     */
    reason?: string;
    /**
     * More detailed reason why status is unavailable
     */
    detail?: string;
}

export interface PutEvseStatusResponse {
    success: true;
}

export interface PutLocationAlertRequest {
    /**
     * error message title
     */
    title?: string;
    /**
     * synthetic alert id
     */
    id?: string;
    /**
     * When alert became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    timestamp: string;
    /**
     * Type/code of the alert
     */
    type: string;
    /**
     * Error message, or other details
     */
    message?: string;
    /**
     * Error code
     */
    errorCode?: string;
}

export interface PutLocationAlertResponse {
    success: true;
}

export interface CeAlert {
    /**
     * error message title
     */
    title?: string;
    /**
     * synthetic alert id
     */
    id?: string;
    /**
     * Error code, if available.
     * e.g. 'kempower:evse:unknown'
     */
    errorCode?: string;
    /**
     * When alert became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    timestamp: string;
    /**
     * Type/code of the alert
     */
    type: string;
    /**
     * EVSE unique identifier.
     */
    evseId: string;
    /**
     * Location this EVSE lies in
     */
    locationUid?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    /**
     * end timestamp for this alert
     */
    endTime?: string;
    /**
     * Error message, or other details
     */
    message?: string;
    payload?: {
        [k: string]: any;
    } | null;
    stationName?: string;
    locationName?: string;
}

export type ChargingStationStatus = 'Faulted' | 'Operative' | 'Unavailable';

export type EVSEStatus = 'Available' | 'Charging' | 'Faulted' | 'Occupied' | 'Reserved' | 'Unavailable' | 'Unknown';

export interface StatusHistoryRow {
    schema: 'statusHistory';
    /**
     * When status became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    statusDate: string;
    /**
     * Status of the EVSE or Charging station.
     */
    status: 'Available' | 'Charging' | 'Faulted' | 'Occupied' | 'Operative' | 'Reserved' | 'Unavailable' | 'Unknown';
    /**
     * Reason for why the status is unavailable
     */
    reason?: string;
    /**
     * More detailed reason why status is unavailable
     */
    detail?: string;
    /**
     * EVSE unique identifier.
     */
    evseId: string;
    /**
     * Location this EVSE lies in
     */
    locationUid?: string;
    /**
     * Name of the evse/connector
     */
    evseName?: string;
    /**
     * Type of the evse/connector
     */
    evseType?: 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
    /**
     * Max connector cable current, e.g. 100 or 125 A
     */
    maxCableCurrent?: number;
    /**
     * Maximum power in kilowatts that this connector could output
     */
    maxPowerKw?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
     */
    maxPowerkWPresent?: number;
    /**
     * Maximum power in kilowatts that this connector could output (unrounded figure)
     */
    maxPowerkWActual?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
     */
    maxPowerkWPresentActual?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed
     */
    maxPowerKwWithoutDynamicLimit?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
     */
    maxPowerKwWithoutDynamicLimitActual?: number;
    /**
     * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
     * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
     * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
     */
    actualEVSEId?: number;
    /**
     * Maximum voltage this EVSE can output (500 or 800V)
     */
    maxVoltage?: number;
    /**
     * Used to deduce whether or not the evse is currently in active configuration
     */
    configurationId?: number;
    /**
     * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
     */
    MaxPowerDisplay?: string;
    /**
     * Config -Short additional text to display on the charger
     */
    SupplementaryNote?: string;
    tariffIds?: string[] | null;
    /**
     * Connector Geo latitude
     */
    lat?: number;
    /**
     * Connector Geo longitude
     */
    lng?: number;
    /**
     * True if energy meter is available for this connector.
     */
    eMeterPresent?: boolean | null;
    /**
     * ID of energy meter, if known.
     */
    eMeterId?: string | null;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string | null;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    _gsi2pk: string;
    _gsi2sk: string;
    /**
     * DynamoDB TTL (Time to Live) - item expiry time (unix timestamp).
     * Set for scheduled deletion of item row.
     * Please note this is SECONDS (unix timestamp) not MILLISECONDS (like usually in javascript).
     */
    _ttl?: number;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionCreatedUserRole: string;
    versionCreatedTenantId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    deleted?: boolean;
}

export type EVSEType = 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
